/** Last-4 digits from AIM `idMasked`, or null when missing. */
export function accountLast4Digits(idMasked: string | null | undefined): string | null {
  if (!idMasked) {
    return null;
  }
  const digits = idMasked.replace(/\*/g, '').trim().slice(-4);
  return digits || null;
}

/** Display last-4 as in the inventory design (`•••• 6105`); missing → `---`. */
export function formatAccountLast4(idMasked: string | null | undefined): string {
  const digits = accountLast4Digits(idMasked);
  return digits ? `•••• ${digits}` : '---';
}

/** Inventory display text. Missing, blank, or malformed values use the data-quality placeholder. */
export function formatAccountField(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value : '—';
}

/** Currency for the Balance column. Explicit null → n/a; missing/malformed → —. */
export function formatAccountBalance(balance: number | null | undefined): string {
  if (balance === null) {
    return 'n/a';
  }
  if (balance === undefined || !Number.isFinite(balance)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(balance);
}

/** Available Balance / money fields in detail — always two fraction digits. Null → n/a. */
export function formatAccountBalanceExact(balance: number | null | undefined): string {
  if (balance === null || balance === undefined || !Number.isFinite(balance)) {
    return 'n/a';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(balance);
}

/** AIM dates (YYYY-MM-DD or ISO) → `Feb 10, 2026`. */
/** Signed ledger amount (+ credit / − debit). Credits are green in the UI. Never send this string to Amplitude. */
export function formatTransactionAmount(amount: number): { text: string; inflow: boolean } {
  const absolute = formatAccountBalanceExact(Math.abs(amount));
  if (amount > 0) {
    return { text: `+${absolute}`, inflow: true };
  }
  if (amount < 0) {
    return { text: `-${absolute}`, inflow: false };
  }
  return { text: absolute, inflow: false };
}

export function transactionStatusSeverity(
  status: string
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  switch (status.trim().toLowerCase()) {
    case 'pending':
    case 'initiated':
    case 'scheduled':
    case 'processing':
      return 'warn';
    case 'disputed':
    case 'voided':
    case 'canceled':
    case 'cancelled':
    case 'missed':
    case 'missed payment':
      return 'danger';
    case 'partially complete':
    case 'partial_complete':
      return 'warn';
    case 'settled':
    case 'complete':
      return 'secondary';
    case 'paused':
      return 'info';
    default:
      return 'info';
  }
}

export function formatAccountDate(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) {
    return 'n/a';
  }
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  const date = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return 'n/a';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function isErrorStatus(status: string | null | undefined): boolean {
  return status?.toLowerCase() === 'error';
}

export function statusTagSeverity(
  status: string | null | undefined
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'verified':
      return 'success';
    case 'approved':
    case 'closed':
      return 'info';
    case 'declined':
    case 'error':
      return 'danger';
    default:
      return 'secondary';
  }
}
